import { useCallback, useMemo, useRef, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import type { OpenAPI } from "simplydo/interfaces"

import api, { asQuery } from "api"
import util from "utils/utils"

import { reducePath } from "./useIdeaEditor"

type Assessment = OpenAPI.GET<"/ideas/{id}">["response"]["assessments"][0]

export const useAssessmentEditor = (idea: OpenAPI.GET<"/ideas/{id}">["response"]) => {
  const [isSaving, setIsSaving] = useState(false)
  const ideaId = idea?._id
  const isAssessor = idea?.isAssessor
  const editTimeout = useRef(null)
  const { t } = useTranslation()
  const [params, setSearchParams] = useSearchParams()

  const assessmentRounds = idea?.ownerIdeaTemplate?.assessments?.rounds || []
  const paramsRoundId = params.get("assessmentRound") || ""
  if (idea?.ownerIdeaTemplate && paramsRoundId) {
    const roundExists = assessmentRounds.some((round) => round.id === paramsRoundId)
    if (!roundExists) {
      setSearchParams((prevParams) => {
        prevParams.delete("assessmentRound")
        return prevParams
      })
    }
  }
  const assessmentRoundId = paramsRoundId || assessmentRounds?.[0]?.id || "legacy"

  const assessmentQuery = useQuery({
    queryKey: ["ideas", ideaId, "assessment", assessmentRoundId],
    queryFn: asQuery(api.ideas.getAssessment, {
      params: [ideaId, assessmentRoundId],
    }),
    enabled: !!ideaId && isAssessor,
  })

  const setAssessmentRound = useCallback(
    (newRoundId: string) => {
      setSearchParams((prevParams) => {
        prevParams.set("assessmentRound", newRoundId)
        return prevParams
      })
    },
    [setSearchParams],
  )

  const clearAssessmentRound = useCallback(() => {
    setSearchParams((prevParams) => {
      prevParams.delete("assessmentRound")
      return prevParams
    })
  }, [setSearchParams])

  const saveAssessment = useCallback(
    (updatedAssessment) => {
      setIsSaving(true)
      api.ideas.updateAssessment(
        ideaId,
        assessmentRoundId,
        updatedAssessment,
        () => {
          setIsSaving(false)
          api.queryClient.invalidateQueries({
            queryKey: ["challenges", idea?.challenge, "assessments"],
          })
        },
        (err) => {
          toast.error(err.message)
          setIsSaving(false)
        },
      )
    },
    [ideaId, idea?.challenge, assessmentRoundId],
  )

  /*
    Path is as follows: ['assessment_object', 'field_id', 'property']
  */
  const updateAssessment = useCallback(
    (update, path, suppressSave = false) => {
      const prevAssessment = assessmentQuery?.data || {}
      const updatedAssessment = reducePath(prevAssessment, update, path) as Assessment

      api.queryClient.setQueryData(["ideas", ideaId, "assessment", assessmentRoundId], updatedAssessment)
      api.queryClient.setQueryData(["challenges", idea?.challenge, "assessments"], (oldData: any) => {
        if (!oldData) return oldData

        const newIdeas = oldData?.ideas?.map((oldIdea) => {
          if (oldIdea._id === ideaId) {
            return {
              ...oldIdea,
              assessments: oldIdea.assessments.map((oldAssessment) => {
                if (
                  oldAssessment.user === updatedAssessment.user &&
                  (oldAssessment.assessmentRoundId || "legacy") === (assessmentRoundId || "legacy")
                ) {
                  return updatedAssessment
                }
                return oldAssessment
              }),
            }
          }
          return oldIdea
        })
        return {
          ...oldData,
          ideas: newIdeas,
        }
      })

      if (editTimeout.current) {
        clearTimeout(editTimeout.current)
        editTimeout.current = null
      }
      editTimeout.current = setTimeout(() => {
        // Second item in the path will be the field id, if saving
        if (!suppressSave) saveAssessment(updatedAssessment)
      }, 400)
    },
    [saveAssessment, ideaId, idea?.challenge, assessmentQuery?.data, assessmentRoundId],
  )

  const submitAssessment = useCallback(() => {
    let scoredAll = true

    const prompt = assessmentQuery?.data?.conflictOfInterest?.hasConflictOfInterest ? (
      <div>
        Once submitted you will not be able to change your assessment of this {t("generic.idea")}.
        <br />
        <br />
        You have declared a conflict of interest. When you submit this assessment, no assessment scores will be
        submitted and the {t("generic.challenge")} managers will be notified. Are you sure you want to proceed?
      </div>
    ) : (
      <div>
        Once submitted you will not be able to change your assessment of this {t("generic.idea")}. You have given the
        following scores:
        <ul>
          {idea?.ownerIdeaTemplate?.body.flatMap((section) => {
            if (
              section.type !== "assessment" ||
              (section.assessmentRoundId || "legacy") !== (assessmentRoundId || "legacy")
            ) {
              return []
            }

            return section.fields
              .filter((field) => field.type === "assessment")
              .map((field) => {
                const fieldScore = assessmentQuery?.data?.assessment[field.id]?.score
                // If the assessment field has not been scored at all it is undefined - only in this case we reject submission
                if (fieldScore === undefined) {
                  scoredAll = false
                }
                return (
                  <li key={field.title}>
                    {field.title}: {fieldScore ?? 0} / {field.options?.availableScore || 0}
                  </li>
                )
              })
          })}
        </ul>
        If you are happy with these scores, click submit now. If you would like to change anything, please cancel and
        amend.
      </div>
    )

    if (!scoredAll) {
      toast.error("You must score all fields before submitting")
      return
    }

    util
      .confirm("Ready to submit your assessment?", prompt)
      .then(() => {
        setIsSaving(true)
        const loadingId = toast.loading("Submitting assessment...")

        api.ideas.submitAssessment(
          ideaId,
          assessmentRoundId,
          ({ assessment: newAssessment }) => {
            updateAssessment(newAssessment.isSubmitted, ["isSubmitted"], true)
            updateAssessment(newAssessment.submittedAt, ["submittedAt"], true)
            api.queryClient.invalidateQueries({
              queryKey: ["challenges", idea?.challenge, "assessments"],
            })
            api.queryClient.invalidateQueries({
              queryKey: ["ideas", ideaId, "assessment", assessmentRoundId],
            })
            toast.success("Assessment submitted", {
              id: loadingId,
            })
            setIsSaving(false)
          },
          (err) => {
            toast.error(`Unable to submit your assessment: ${err.message}`, {
              id: loadingId,
            })
            setIsSaving(false)
          },
        )
      })
      .catch(() => {})
  }, [idea, ideaId, assessmentRoundId, updateAssessment, assessmentQuery.data, t])

  return useMemo(() => {
    return {
      isAssessmentLoading: assessmentQuery.isLoading,
      assessmentState: assessmentQuery.data,
      assessmentIsSubmissable: util.assessmentIsSubmissable(assessmentQuery.data, idea?.ownerIdeaTemplate),
      updateAssessment,
      submitAssessment,
      isSavingAssessment: isSaving,
      setAssessmentRound,
      clearAssessmentRound,
    }
  }, [assessmentQuery, idea, isSaving, submitAssessment, updateAssessment, setAssessmentRound, clearAssessmentRound])
}
